import { Skeleton } from '@bambi/component-library/src/components/Skeleton/Skeleton';
import { classNames } from '@bambi/component-library/src/utils/classNames';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { matchPath, NavLink, useLocation, useNavigate } from 'react-router';

import NavBadge from './NavBadge';

type Tab = {
  id?: string;
  name: string;
  href: string;
  badgeCount?: number;
  matcher?: string;
};

type TabNavigationProps = {
  tabs: Tab[];
  isLoading?: boolean;
  showDeleteIcon?: boolean;
  onDelete?: (id: string) => void;
};

export function TabNavigation({
  tabs,
  isLoading,
  showDeleteIcon,
  onDelete,
}: TabNavigationProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  // no tab is clickable until the set is settled, so none can move under a click
  if (isLoading) {
    return <TabNavigationSkeleton tabs={tabs} />;
  }
  return (
    <div>
      <div className="lg:hidden">
        <label htmlFor="selected-tab" className="sr-only">
          Select a tab
        </label>
        <select
          id="selected-tab"
          name="selected-tab"
          className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-purple-500 focus:outline-none focus:ring-purple-500 sm:text-sm"
          defaultValue={
            tabs.find((tab) =>
              matchPath(
                { path: tab.matcher ? tab.matcher : tab.href },
                currentPath
              )
            )?.name
          }
          onChange={(e) => {
            const nextHref = e.target.value;
            navigate(nextHref);
          }}
        >
          {tabs.map((tab) => (
            <option key={tab.id ?? tab.name} value={tab.href}>
              {tab.name}
            </option>
          ))}
        </select>
      </div>
      <div className="hidden lg:block">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex items-center space-x-8">
            {tabs.map((tab) => (
              <div
                key={tab.id ?? tab.name}
                data-testid={`tab-navigation-item-${tab.name}`}
                className={classNames(
                  matchPath(
                    { path: tab.matcher ? tab.matcher : tab.href },
                    currentPath
                  )
                    ? 'is-active border-primary-700 text-primary-700'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  'group flex cursor-pointer items-center space-x-2 whitespace-nowrap border-b-2 text-sm font-medium ',
                  showDeleteIcon && 'pr-6 hover:pr-0'
                )}
              >
                <NavLink
                  data-testid={`tab-navigation-link-${tab.name}`}
                  to={tab.href}
                  end
                  className="relative py-4 px-1"
                >
                  {tab.name}
                  {/* out of flow so a late count cannot shift the tabs right of
                      it, and anchored left so extra digits grow into the gap
                      rather than over the label */}
                  {!!tab.badgeCount && (
                    <div className="absolute top-1 left-full ml-0.5">
                      <NavBadge count={tab.badgeCount} />
                    </div>
                  )}
                </NavLink>
                {showDeleteIcon && (
                  <XMarkIcon
                    data-testid="tab-navigation-delete-icon"
                    className="hidden h-4 w-4 text-gray-500 group-hover:block"
                    onClick={() => tab.id && onDelete && onDelete(tab.id)}
                  />
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}

// Mirrors the two breakpoints the loaded component renders so the row keeps its
// shape at both, and lays each bar over an invisible copy of the real label so
// the bars match the typography they stand in for.
function TabNavigationSkeleton({ tabs }: { tabs: Tab[] }) {
  return (
    <div>
      <div className="lg:hidden">
        <div className="relative mt-1 block w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-base sm:text-sm">
          <span className="invisible">&nbsp;</span>
          <span className="absolute inset-y-0 right-10 left-3 flex items-center">
            <Skeleton />
          </span>
        </div>
      </div>
      <div className="hidden lg:block">
        <div className="border-b border-gray-200">
          <nav
            className="-mb-px flex items-center space-x-8"
            data-testid="tab-navigation-skeleton"
          >
            {tabs.map((tab) => (
              <div
                key={tab.id ?? tab.name}
                className="flex items-center whitespace-nowrap border-b-2 border-transparent text-sm font-medium"
              >
                <span className="relative py-4 px-1">
                  <span className="invisible">{tab.name}</span>
                  <span className="absolute inset-x-1 top-1/2 flex -translate-y-1/2 items-center">
                    <Skeleton />
                  </span>
                </span>
              </div>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}
